import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Package, Layers, Loader2, ImageIcon, ArrowLeft } from "lucide-react";
import { SceneQuestions, visibleEntries } from "./SceneQuestions";
import { getRegionDisplay } from "@/lib/regions";
import {
  scenesForProductType, camerasForScene, type ConfigLink,
} from "@/lib/sceneCompatibility";

export interface SceneOption { id: string; name: string; regionKey?: string | null; config?: any }
export interface NamedOption { id: string; name: string }
export interface ProductOption extends NamedOption {
  /** wizard_configs id of the product *type*, from product_params._config_id. */
  configId?: string | null;
  thumbnailUrl?: string | null;
}

interface SceneStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenes: SceneOption[];
  products: ProductOption[];
  cameras: NamedOption[];
  /** config_links rows, loaded once by the page. */
  links: ConfigLink[];
  /** Preselect when the user arrived from a product. */
  initialProductId?: string | null;
  /**
   * "start" opens a new session; "attach" sets the scene up on the one already
   * open. Same three questions either way — only the wording differs, because
   * "Open scene" reads like it discards what you have.
   */
  mode?: "start" | "attach";
  onStart: (choice: {
    sceneId: string; productId: string; cameraId: string;
    answers: Record<string, string>;
  }) => Promise<void> | void;
}

/**
 * The "start from a scene" entry point.
 *
 * A scene is a product photographed a particular way, so all three parts are
 * required up front rather than discovered as errors later. Previously the
 * product requirement was enforced inside the composer (and again server-side),
 * which meant a user could answer every question in the scene and only then be
 * told a product was needed.
 *
 * The three choices narrow each other, because `config_links` already records
 * which scenes belong to which product type and which camera styles each scene
 * supports. Studio never read it — only the product editor did, and only in the
 * opposite direction — so it was possible to put an indoor scene on an outdoor
 * unit, or pick any of sixteen camera styles when the scene supports five.
 * Product is asked first because it is the fact the user actually has: they
 * know what they are photographing before they know how.
 *
 * A scene or camera with no links is treated as universal rather than hidden —
 * links are a constraint where they exist, not a whitelist everything must
 * join.
 *
 * Free prompting has no such requirement and does not come through here.
 */
export function SceneStartDialog({
  open, onOpenChange, scenes, products, cameras, links, initialProductId, onStart, mode = "start",
}: SceneStartDialogProps) {
  const [sceneId, setSceneId] = useState<string>("");
  const [productId, setProductId] = useState<string>("");
  const [cameraId, setCameraId] = useState<string>("");
  const [starting, setStarting] = useState(false);
  /**
   * The questions come second, in the same modal.
   *
   * Setting a scene up is one task, and it used to end halfway: the dialog
   * closed after three dropdowns and left the six questions behind it in a
   * panel, which reads as "done" when it isn't. Editing later still happens
   * inline — that is a different moment, and a modal for changing one answer
   * would be heavy.
   */
  const [step, setStep] = useState<1 | 2>(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // Reset per opening so a cancelled attempt doesn't leak into the next one,
  // except the product, which the caller may have supplied.
  // Depends on `open` alone: `scenes` and `cameras` are new array identities on
  // every parent render, so including them would re-run this constantly and
  // wipe the user's choices mid-selection.
  useEffect(() => {
    if (!open) return;
    setProductId(initialProductId || "");
    setSceneId("");
    setCameraId("");
    setStarting(false);
    setStep(1);
    setAnswers({});
  }, [open, initialProductId]);


  const selectedProduct = products.find((p) => p.id === productId);

  const availableScenes = useMemo(
    () => scenesForProductType(scenes, links, selectedProduct?.configId),
    [scenes, links, selectedProduct?.configId],
  );
  const availableCameras = useMemo(
    () => camerasForScene(cameras, links, sceneId),
    [cameras, links, sceneId],
  );

  // Changing the product can invalidate the scene, and the scene the camera.
  // And where the narrowing leaves exactly one option, take it: a required
  // field with a single valid answer is a question not worth asking.
  // Answers belong to one scene's questions; a different scene asks different
  // ones, and keeping them would silently apply a stale value to a same-named
  // field.
  useEffect(() => { setAnswers({}); }, [sceneId]);

  useEffect(() => {
    if (sceneId && !availableScenes.some((s) => s.id === sceneId)) { setSceneId(""); return; }
    if (!sceneId && productId && availableScenes.length === 1) setSceneId(availableScenes[0].id);
  }, [availableScenes, sceneId, productId]);
  useEffect(() => {
    if (cameraId && !availableCameras.some((c) => c.id === cameraId)) { setCameraId(""); return; }
    if (!cameraId && sceneId && availableCameras.length === 1) setCameraId(availableCameras[0].id);
  }, [availableCameras, cameraId, sceneId]);

  const ready = !!sceneId && !!productId && !!cameraId;

  const selectedScene = availableScenes.find((sc) => sc.id === sceneId);
  const entries = selectedScene ? visibleEntries(selectedScene.config, answers) : [];
  const answered = entries.filter((e) => answers[e.key]).length;

  const start = async () => {
    if (!ready || starting) return;
    setStarting(true);
    try {
      await onStart({ sceneId, productId, cameraId, answers });
    } finally {
      setStarting(false);
    }
  };

  const missing = [
    !productId && "a product",
    !sceneId && "a scene",
    !cameraId && "a camera style",
  ].filter(Boolean) as string[];

  // "a scene and a product and a camera style" reads like a machine wrote it.
  const missingText =
    missing.length > 1
      ? `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`
      : missing[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={step === 2 ? "sm:max-w-2xl" : "sm:max-w-md"}>
        <DialogHeader>
          <DialogTitle>
            {step === 2
              ? selectedScene?.name || "Set up the scene"
              : mode === "attach" ? "Add a scene" : "Start from a scene"}
          </DialogTitle>
          <DialogDescription>
            A scene sets up the shot for you — you answer a few questions and it
            writes the prompt. It needs a product to photograph and a camera
            style to shoot it in.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
        <div className="space-y-4 py-1">
          <Field icon={<Package className="w-3.5 h-3.5" />} label="Product">
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger><SelectValue placeholder="Choose a product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <ProductThumb product={p} />
                      <span className="truncate">{p.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            icon={<Layers className="w-3.5 h-3.5" />}
            label="Scene"
            hint={
              productId && availableScenes.length < scenes.length
                ? `${availableScenes.length} of ${scenes.length} suit this product`
                : undefined
            }
          >
            <Select value={sceneId} onValueChange={setSceneId} disabled={!productId}>
              <SelectTrigger>
                <SelectValue placeholder={productId ? "Choose a scene" : "Pick a product first"} />
              </SelectTrigger>
              <SelectContent>
                {availableScenes.map((sc) => {
                  const region = getRegionDisplay(sc.regionKey ?? null);
                  return (
                    <SelectItem key={sc.id} value={sc.id}>
                      {/* Which market a scene is written for is not in its
                          name, and it decides the whole look. */}
                      <span className="flex items-center gap-2">
                        <span aria-hidden>{region.emoji}</span>
                        <span className="truncate">{sc.name}</span>
                        <span className="text-xs text-muted-foreground">{region.label}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </Field>

          <Field
            icon={<Camera className="w-3.5 h-3.5" />}
            label="Camera style"
            hint={
              sceneId && availableCameras.length < cameras.length
                ? `${availableCameras.length} of ${cameras.length} suit this scene`
                : undefined
            }
          >
            <Select value={cameraId} onValueChange={setCameraId} disabled={!sceneId}>
              <SelectTrigger>
                <SelectValue placeholder={sceneId ? "Choose a camera style" : "Pick a scene first"} />
              </SelectTrigger>
              <SelectContent>
                {availableCameras.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        )}

        {step === 2 && (
          <div className="max-h-[58vh] overflow-y-auto pr-1 py-1">
            <SceneQuestions entries={entries} answers={answers} columns={2}
              onChange={(key, value) => setAnswers((prev) => ({ ...prev, [key]: value }))} />
          </div>
        )}

        <DialogFooter className="sm:justify-between sm:items-center gap-2">
          {step === 1 ? (
            <>
              {/* Say what is missing rather than leaving a dead button unexplained. */}
              <span className="text-xs text-muted-foreground">
                {ready ? "You can change any of these later." : `Still needs ${missingText}.`}
              </span>
              <Button
                onClick={() => setStep(2)}
                disabled={!ready}
                className="gradient-primary text-primary-foreground"
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="gap-1.5">
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </Button>
                {/* Every question is optional — an unanswered one is left to
                    the model — so this reports rather than blocks. */}
                <span className="text-xs text-muted-foreground">
                  {entries.length === 0
                    ? "This scene asks nothing — it is ready."
                    : answered === entries.length
                      ? `All ${entries.length} answered.`
                      : `${answered} of ${entries.length} answered · rest left to the model`}
                </span>
              </span>
              <Button
                onClick={() => void start()}
                disabled={!ready || starting}
                className="gradient-primary text-primary-foreground"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {mode === "attach" ? "Add scene" : "Open scene"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ icon, label, hint, children }: {
  icon: JSX.Element; label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon}{label}
        {/* Say when the list has been narrowed. A shorter menu with no
            explanation reads as missing data. */}
        {hint && <span className="font-normal text-[11px] opacity-80">· {hint}</span>}
      </span>
      {children}
    </div>
  );
}

/**
 * A product is recognised by sight long before its name is read, and several
 * here differ only by a colour word at the end ("Stylish 2 - Black" / "- White"
 * / "- Silver"). The placeholder keeps the row height steady so the list does
 * not jump as images arrive.
 */
function ProductThumb({ product }: { product: ProductOption }) {
  return (
    <span className="w-7 h-7 rounded-md bg-muted overflow-hidden shrink-0 flex items-center justify-center">
      {product.thumbnailUrl ? (
        <img
          src={product.thumbnailUrl}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <ImageIcon className="w-3.5 h-3.5 text-muted-foreground/50" />
      )}
    </span>
  );
}
